import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tab } from 'semantic-ui-react';
import { KlimaProgramService } from '~/api/services/klimaProgram';
import Layout from '~/components/Layout';
import notify from '~/components/Notify';
import Dialog from '~/components/Dialog';
import { b64toBlob, handleErrors } from '~/utils/helpers';

// Tabs
import TermsAndConditions from './Tabs/TermsAndConditions';
import { StyledMenuItem, StyledTabPane } from './style';
import FeedSuppliers from './Tabs/FeedSuppliers';
import FeedProducts from './Tabs/FeedProducts';

const KlimaProgram: React.FC = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [fileSource, setFileSource] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const methods = useForm({ mode: 'onSubmit' });
  const { control, getValues } = methods;

  const watchFile = useWatch({ control, name: 'file' });

  useEffect(() => {
    fetchKlimaProgramData();
  }, []);

  async function fetchKlimaProgramData() {
    setIsBusy(true);
    try {
      const response = await KlimaProgramService.edit();
      methods.reset(response.data);
    } catch (error) {
      handleErrors(error);
    }
    setIsBusy(false);
  }

  async function onSubmit(data) {
    setIsBusy(true);
    try {
      await KlimaProgramService.update(data);
      await fetchKlimaProgramData();
      notify(t('Climate program settings updated'), 'success');
    } catch (error) {
      handleErrors(error);
    }
    setIsBusy(false);
  }

  async function handleDownloadPdf() {
    setIsBusy(true);
    try {
      if (watchFile) {
        setFileSource(watchFile);
      } else {
        const fileName = getValues('filename');
        if (fileName) {
          const response = await KlimaProgramService.getFile({ fileName });
          const type = response.data.split(';')[0].split('data:')[1];
          const blob = b64toBlob(response.data, type);
          const blobUrl = URL.createObjectURL(blob);
          setFileSource(blobUrl);
        }
      }
    } catch (error) {
      handleErrors(error);
    }
    setIsBusy(false);
  }

  function renderModalPreviewFile() {
    return (
      <Dialog
        open={Boolean(fileSource)}
        handleClose={() => setFileSource(null)}
        textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
        textHeader={t('Preview')}
        textClose={t('Close')}
        type={'preview'}
      />
    );
  }

  function getMenuItem(pane) {
    return (
      <StyledMenuItem key={pane.menuItem} className='tab-item'>
        <div>{pane.menuItem}</div>
      </StyledMenuItem>
    );
  }

  function renderTabs() {
    const panes = [
      {
        menuItem: t('Allgemeine Geschäftsbedingungen'),
        component: <TermsAndConditions onPreviewFile={handleDownloadPdf} />
      },
      {
        menuItem: t('Feed Suppliers'),
        component: <FeedSuppliers />
      },
      {
        menuItem: t('Feed Products'),
        component: <FeedProducts />
      }
    ];

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeIndex}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex as number)}
        renderActiveOnly={false}
        panes={panes.map((pane, index) => ({
          menuItem: getMenuItem(pane),
          pane: (
            <StyledTabPane
              // eslint-disable-next-line
              children={pane.component}
              active={index === activeIndex}
              key={pane.menuItem}
            />
          )
        }))}
      />
    );
  }

  const showUpdateButton = activeIndex === 0;

  return (
    <Layout
      onSubmit={showUpdateButton ? methods.handleSubmit(onSubmit) : undefined}
      title={t('Clima program settings')}
      segmented
      isBusy={isBusy}
      action={showUpdateButton ? t('Update') : undefined}
    >
      {!isBusy && (
        <FormProvider {...methods}>
          {fileSource && renderModalPreviewFile()}
          {renderTabs()}
        </FormProvider>
      )}
    </Layout>
  );
};

export default KlimaProgram;
